import React, { useState } from 'react';
import styled from 'styled-components';
import { getAuth } from 'firebase/auth';
import { API_URL } from '../../constants/string-constants';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: var(--clr-prim);
  padding: 2rem;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  color: var(--clr-sec);
  margin: 0;
  font-size: 1.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--clr-sec);
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const MessageInfo = styled.div`
  background-color: var(--clr-bg);
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  border-left: 4px solid #ff6b35;
`;

const MessageText = styled.p`
  margin: 0;
  color: var(--clr-sec);
  font-style: italic;
  word-break: break-word;
`;

const UserIdText = styled.p`
  margin: 0.5rem 0 0 0;
  color: var(--clr-sec);
  font-family: monospace;
  font-size: 0.8rem;
  opacity: 0.7;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: var(--clr-sec);
  font-weight: 500;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background-color: white;
  color: #333;
  
  &:focus {
    outline: none;
    border-color: #ff6b35;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  background-color: white;
  color: #333;
  
  &:focus {
    outline: none;
    border-color: #ff6b35;
  }
  
  &::placeholder {
    color: #999;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background-color: #666;
  color: white;
  
  &:hover:not(:disabled) {
    background-color: #555;
  }
`;

const ReportButton = styled(Button)`
  background-color: #ff6b35;
  color: white;
  
  &:hover:not(:disabled) {
    background-color: #e55a2b;
  }
`;

const ErrorMessage = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 0.75rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  border: 1px solid #f5c6cb;
`;

const SuccessMessage = styled.div`
  background-color: #d4edda;
  color: #155724;
  padding: 0.75rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  border: 1px solid #c3e6cb;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background-color: var(--clr-bg);
  border-radius: 8px;
  margin-bottom: 1.5rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #ff6b35;
`;

const CheckboxLabel = styled.label`
  color: var(--clr-sec);
  font-weight: 500;
  cursor: pointer;
  flex: 1;
  user-select: none;
`;

const REPORT_REASONS = [
  { value: 'spam', label: 'Spam' },
  { value: 'harassment', label: 'Harassment or bullying' },
  { value: 'inappropriate_content', label: 'Inappropriate content' },
  { value: 'hate_speech', label: 'Hate speech' },
  { value: 'threatening_behavior', label: 'Threatening behavior' },
  { value: 'personal_information', label: 'Sharing personal information' },
  { value: 'impersonation', label: 'Impersonation' },
  { value: 'other', label: 'Other' }
];

const ReportMessageModal = ({ isOpen, onClose, message, room = 'general', messageType = 'room', onBlock }) => {
  const [reason, setReason] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [shouldBlock, setShouldBlock] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!reason) {
      setError('Please select a reason for the report');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = await getAuth().currentUser.getIdToken();
      
      const response = await fetch(`${API_URL}report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          message_id: message.id,
          reason: reason,
          additionalContext: additionalContext.trim() || null,
          room: room,
          messageType: messageType,
        }),
      });

      if (response.ok) {
        const result = await response.json();

        // Block the user if checkbox is checked and onBlock is provided
        if (shouldBlock && onBlock) {
          onBlock(message);
        }

        setSuccess('Report submitted successfully. Thank you for helping keep our community safe.');
        setReason('');
        setAdditionalContext('');
        setShouldBlock(true);

        // Auto-close after 2 seconds
        setTimeout(() => {
          onClose();
          setSuccess('');
        }, 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to submit report');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setAdditionalContext('');
    setShouldBlock(true);
    setError('');
    setSuccess('');
    onClose();
  };

  if (!isOpen || !message) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Report Message</ModalTitle>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </ModalHeader>

        <MessageInfo>
          <MessageText>"{message.messageText}"</MessageText>
          <UserIdText>From: {message.nickname || 'Anonymous'}</UserIdText>
        </MessageInfo>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="reason">Why are you reporting this message? *</Label>
            <Select
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              disabled={loading}
            >
              <option value="">Select a reason...</option>
              {REPORT_REASONS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="context">Additional context (optional)</Label>
            <TextArea
              id="context"
              value={additionalContext}
              onChange={(e) => setAdditionalContext(e.target.value)}
              placeholder="Provide any additional details that might help moderators understand the issue..."
              disabled={loading}
            />
          </FormGroup>

          {onBlock && (
            <CheckboxGroup
              onClick={() => !loading && setShouldBlock(!shouldBlock)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !loading) {
                  e.preventDefault();
                  setShouldBlock(!shouldBlock);
                }
              }}
            >
              <Checkbox
                type="checkbox"
                id="shouldBlock"
                checked={shouldBlock}
                onChange={() => {}}
                disabled={loading}
                tabIndex={-1}
              />
              <CheckboxLabel>
                Also block this user
              </CheckboxLabel>
            </CheckboxGroup>
          )}

          <ButtonGroup>
            <CancelButton type="button" onClick={handleClose} disabled={loading}>
              Cancel
            </CancelButton>
            <ReportButton type="submit" disabled={loading || !reason}>
              {loading ? 'Submitting...' : 'Submit Report'}
            </ReportButton>
          </ButtonGroup>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ReportMessageModal;